import { Button, AppBar, Toolbar, Typography, Box, Container, Chip } from '@mui/material';
import { Navigate, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import SideNav from '../../Components/SideNav';
import Login from '../Auth/Login';
import Horse from '../Horse';
import Riders from '../Riders';
import Clubs from '../Clubs';
import ClubDetails from '../Clubs/Details';
import Category from '../Clubs/Category';
import EventResults from '../Clubs/Results';
import HorseDetails from '../Horse/Details';
import RiderDetails from '../Riders/Details';
import Calendar from '../Calendar';
import Users from '../Users';
import MarketPlace from '../MarketPlace';
import Dashboard from '../Dashboard';
import Analytics from '../Analytics';
import Transactions from '../Transactions';
import VideoAnalytics from '../VideoAnalytics';
import SessionalPoints from '../SessionalPoints';

const PAGE_META = {
  '/riders':           { title: 'Riders',          emoji: '🏇', color: '#3b82f6' },
  '/horse':            { title: 'Horses',           emoji: '🐎', color: '#10b981' },
  '/clubs':            { title: 'Clubs',            emoji: '🏆', color: '#ec4899' },
  '/calendar':         { title: 'Calendar',         emoji: '📅', color: '#f97316' },
  '/users':            { title: 'Users',            emoji: '👥', color: '#8b5cf6' },
  '/dashboard':        { title: 'Dashboard',        emoji: '📊', color: '#0ea5e9' },
  '/marketplace':      { title: 'Marketplace',      emoji: '🛒', color: '#eab308' },
  '/analytics':        { title: 'Analytics',        emoji: '📈', color: '#22c55e' },
  '/transactions':     { title: 'Transactions',     emoji: '💳', color: '#ef4444' },
  '/video-analytics':  { title: 'Video Analytics',  emoji: '🎬', color: '#a855f7' },
  '/sessional-points': { title: 'Sessional Points', emoji: '⭐', color: '#f59e0b' },
};

function getPageMeta(pathname) {
  for (const [key, meta] of Object.entries(PAGE_META)) {
    if (pathname.startsWith(key)) return meta;
  }
  return { title: 'Equios Admin', emoji: '⚡', color: '#6366f1' };
}

const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = () => {
    localStorage.removeItem('user');
    navigate('/admin-login');
  };

  const isLoggedIn = localStorage.getItem('user');
  const meta = getPageMeta(location.pathname);

  if (!isLoggedIn) {
    return (
      <Routes>
        <Route path="/" element={<Navigate to="/admin-login" />} />
        <Route path="/admin-login" element={<Login />} />
      </Routes>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <SideNav />
      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        {/* Top accent strip */}
        <Box
          sx={{
            height: 3,
            background: `linear-gradient(90deg, ${meta.color}, ${meta.color}aa, transparent)`,
            flexShrink: 0,
          }}
        />

        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: 'rgba(255,255,255,0.95)',
            backdropFilter: 'blur(12px)',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            top: 0,
          }}
        >
          <Toolbar
            sx={{
              minHeight: { xs: 56, sm: 60 },
              px: { xs: 2, sm: 3 },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${meta.color}22, ${meta.color}44)`,
                  border: `1px solid ${meta.color}33`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.1rem',
                  flexShrink: 0,
                }}
              >
                {meta.emoji}
              </Box>
              <Typography
                variant="h6"
                component="h1"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  letterSpacing: '-0.02em',
                  fontSize: { xs: '1rem', sm: '1.15rem' },
                }}
              >
                {meta.title}
              </Typography>
            </Box>

            <Button
              onClick={handleSignOut}
              variant="outlined"
              size="small"
              startIcon={<LogoutOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{
                borderColor: 'rgba(0,0,0,0.1)',
                color: 'text.secondary',
                fontWeight: 600,
                fontSize: '0.8rem',
                borderRadius: 2,
                '&:hover': {
                  borderColor: '#ef4444',
                  color: '#ef4444',
                  bgcolor: '#fef2f2',
                },
              }}
            >
              Sign out
            </Button>
          </Toolbar>
        </AppBar>

        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            py: { xs: 2, sm: 3 },
            px: { xs: 2, sm: 3 },
          }}
        >
          <Container maxWidth={false} disableGutters sx={{ maxWidth: 1600, mx: 'auto' }}>
            <Routes>
              <Route path="/" element={<Navigate to="/riders" />} />
              <Route path="/riders" element={<Riders />} />
              <Route path="/horse" element={<Horse />} />
              <Route path="/clubs" element={<Clubs />} />
              <Route path="/clubs/:id/:champion_available" element={<ClubDetails />} />
              <Route path="/clubs/category/:id" element={<Category />} />
              <Route path="/clubs/results/:id" element={<EventResults />} />
              <Route path="/horse/details/:id" element={<HorseDetails />} />
              <Route path="/riders/details/:id" element={<RiderDetails />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/calendar" element={<Calendar />} />
              <Route path="/users" element={<Users />} />
              <Route path="/marketplace" element={<MarketPlace />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/transactions" element={<Transactions />} />
              <Route path="/video-analytics" element={<VideoAnalytics />} />
              <Route path="/sessional-points" element={<SessionalPoints />} />
            </Routes>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default MainLayout;
